// The simulated verification UI — a stand-in for `useVideoVerification` that
// makes the badge and the panel look the way they will once the Solana program
// is deployed, so the product can be demonstrated and recorded before it is.
//
// READ fixture/fixtureMode.ts FIRST. Nothing here verifies anything. This
// module computes no hash, opens no socket, and reads no chain; it returns
// the same `VerificationResult` shape the real hook returns, filled from data
// the API already serves plus a scripted delay. It is reachable only when the
// URL carries `?verify=fixture` — see fixtureRequest.
//
// TWO CHOICES IN HERE ARE DELIBERATE AND WORTH KEEPING IF THIS IS EDITED.
//
//  * `verifier` IS ALWAYS undefined. The real hook hands VideoPlayer a
//    SegmentVerifier that hashes each media segment as it streams. Handing one
//    over here would start real hashing against manifests a demo environment
//    may not have, and the first 404 would flip a recording to a red failure
//    banner mid-take. A demo that cannot fail is the whole requirement, so the
//    segment layer is simply absent rather than faked.
//
//  * `failure` IS ALWAYS null AND `rejectedAddenda` ALWAYS EMPTY. Same reason.
//    There is no scripted-failure mode because a failure the UI cannot justify
//    is the one thing worse than a success it cannot justify: a viewer shown a
//    fake tampering alert would reasonably act on it.
//
// The one thing that is NOT invented is the version list: it is projected from
// the container JSON the API really served (`VersionContext.versionList`), so
// a multi-version demo shows the publisher's actual history, with only the
// account addresses synthesised.
import { useEffect, useMemo, useState } from 'react'
import { PublicKey } from '@solana/web3.js'
import type { ContainerVersion, Video } from '../types.js'
import type { AnchorState, ChainRecord } from './useChainRecord.js'
import type { ContainerRecord, VersionConsistency } from './useContainerRecord.js'
import type { VerificationResult } from './useVideoVerification.js'
import { warnOnce } from './fixture/fixtureMode.js'

/** How long the badge sits at "Verifying…" before it settles. Long enough to
 * be visible in a screen recording, short enough that nobody narrating over it
 * has to wait. */
export const SIMULATED_VERIFY_MS = 2200

/** Mirrors CORRECTION_WINDOW in useChainRecord.ts, and through it the Rust
 * program's constant. Only used to compute the dates the panel prints. */
const CORRECTION_WINDOW_SECONDS = 7 * 24 * 60 * 60

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * 32 deterministic bytes from a string, via FNV-1a.
 *
 * Deterministic matters more than it looks: a recording that is re-taken, or a
 * page reloaded mid-demo, must show the SAME addresses and hashes both times.
 * A random fake would change between takes and look, correctly, like nonsense.
 *
 * Not a cryptographic hash and never used as one — its only job is to produce
 * stable, plausible-looking bytes for display.
 */
export function seedBytes(seed: string, length = 32): Uint8Array {
  const out = new Uint8Array(length)
  let h = 0x811c9dc5
  for (let i = 0; i < length; i++) {
    // Re-absorb the seed for every output byte so the bytes do not fall into a
    // short cycle on a short seed.
    for (let j = 0; j < seed.length; j++) {
      h ^= seed.charCodeAt(j) + i
      h = Math.imul(h, 0x01000193) >>> 0
    }
    out[i] = h & 0xff
  }
  return out
}

/** A plausible, stable base58 account address for `seed`. Nothing lives at it
 * on any cluster; the panel links to an explorer, which will say so if anyone
 * clicks through. */
export function fakeAddress(seed: string): string {
  return new PublicKey(seedBytes(seed)).toBase58()
}

/** A stable 64-character lowercase hex string for `seed`. */
export function fakeHashHex(seed: string): string {
  return Array.from(seedBytes(seed), (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * The chain record the panel draws, built from the video's REAL publication
 * record where it has one and synthesised where it does not.
 *
 * `root_hash` in particular is the genuine article whenever the publish job has
 * run — it is the hash the server actually computed over the manifest it
 * actually stored. What is simulated is not the number, it is the claim that a
 * chain attests to it.
 */
export function simulatedChainRecord(video: Video, now: Date = new Date()): ChainRecord {
  const publication = video.publication
  const rootHash = publication?.root_hash || fakeHashHex(`root:${video.id}`)

  // A published-nine-days-ago video is one whose correction window has closed,
  // which is the state worth demonstrating: it renders "Sealed", the strongest
  // and most distinctive of the anchor states. When the API has real dates,
  // those win.
  const publishedAt = parseDate(publication?.published_at) ?? new Date(now.getTime() - 9 * DAY_MS)
  const windowExpires =
    parseDate(publication?.window_expires) ?? new Date(publishedAt.getTime() + CORRECTION_WINDOW_SECONDS * 1000)
  // An explicit seal two days after publication, so the panel prints a real
  // "Sealed <date>" line rather than the window-expiry wording. Overridden by
  // a genuine sealed_at whenever the API has one.
  const sealedAt = parseDate(publication?.sealed_at) ?? new Date(publishedAt.getTime() + 2 * DAY_MS)

  return {
    rootHash,
    state: 'sealed',
    revision: publication?.revision ?? 1,
    publishedAt,
    sealedAt,
    windowExpires,
  }
}

/** Go's zero time marshals as year 1, and an absent field as ''. Both mean
 * "not set" and must not become an Invalid Date the panel would print. */
function parseDate(raw: string | undefined): Date | null {
  if (!raw) return null
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return null
  if (parsed.getUTCFullYear() < 1970) return null
  return parsed
}

/**
 * The container as the panel draws it, projected from the container JSON the
 * API served. Real ids, real indices, real publication dates; only the account
 * addresses are synthesised.
 *
 * Returns null when there is nothing to show, which is what the real hook
 * returns for an unanchored container — the panel then omits its container row
 * and its version list entirely, exactly as it does today.
 */
export function simulatedContainer(
  containerId: string | undefined,
  versions: ContainerVersion[] | undefined,
): ContainerRecord | null {
  if (!containerId || !versions || versions.length === 0) return null
  return {
    containerPda: fakeAddress(`container:${containerId}`),
    versionCount: versions.length,
    versions: versions
      .map((version) => ({
        index: version.index,
        videoId: version.video_id,
        videoPda: fakeAddress(`video:${version.video_id}`),
        addedAt: parseDate(version.added_at) ?? new Date(),
      }))
      .sort((a, b) => a.index - b.index),
  }
}

/**
 * Where the video being played sits in that list. Computed rather than pinned
 * to 'consistent' so a demo of an older version still shows the amber
 * "newer version" chip — which is a thing worth demonstrating, and the one
 * warning state that is safe to show because it accuses nobody of anything.
 */
export function simulatedConsistency(record: ContainerRecord | null, playingVideoId: string): VersionConsistency {
  if (!record || record.versionCount <= 0) return 'unknown'
  const id = playingVideoId.trim().toLowerCase()
  const playing = record.versions.find((version) => version.videoId.trim().toLowerCase() === id)
  if (!playing) return 'unknown'
  return playing.index === record.versionCount - 1 ? 'consistent' : 'superseded'
}

/** What `VerifiedPlayerInner` needs, which is the real hook's result plus the
 * account address the real component derives for itself from an authority the
 * demo does not have. */
export interface SimulatedVerification extends VerificationResult {
  videoPda: string
}

/**
 * The scripted verification. Same signature shape and same return type as
 * `useVideoVerification`, so `VerifiedPlayer` can pick between the two and
 * every component below is untouched.
 */
export function useSimulatedVerification(
  video: Video,
  containerId?: string,
  versionList?: ContainerVersion[],
): SimulatedVerification {
  const [verified, setVerified] = useState(false)

  useEffect(() => {
    warnOnce()
  }, [])

  // Restarts on every video, so navigating between versions during a recording
  // replays the spinner rather than showing an already-settled badge.
  useEffect(() => {
    setVerified(false)
    const timer = setTimeout(() => setVerified(true), SIMULATED_VERIFY_MS)
    return () => clearTimeout(timer)
  }, [video.id])

  const chain = useMemo(() => simulatedChainRecord(video), [video])
  const container = useMemo(() => simulatedContainer(containerId, versionList), [containerId, versionList])
  const versionConsistency = useMemo(() => simulatedConsistency(container, video.id), [container, video.id])
  const videoPda = useMemo(() => fakeAddress(`video:${video.id}`), [video.id])

  return useMemo(
    () => ({
      verify: verified ? 'verified' : 'verifying',
      anchor: 'sealed' as AnchorState,
      chain,
      // See the header: no scripted failures, and no segment verifier.
      failure: null,
      rejectedAddenda: [],
      versionConsistency,
      container,
      verifier: undefined,
      // No sealed base to hand out: there is no chain here to have read one
      // from. The fixture path builds its own fabricated root and rendition
      // (fixture/syntheticLedgerSource.ts) from the player's duration and
      // feeds the ledger with those, so these two stay empty on this path.
      root: null,
      renditions: [],
      videoPda,
    }),
    [verified, chain, versionConsistency, container, videoPda],
  )
}
